<script>
import Swal from 'sweetalert2';
import InfoPedido from '@/components/catalogo-produtos/InfoPedido.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import InitialDataService from '@/services/InitialData.service';
import { isPerfilClienteAtivo, isPerfilRepresentanteAtivo } from '@/libs/perfilCliente';
import { title } from 'echarts/lib/theme/dark';

export default {
  name: 'ModalInfoPedido',
  components: { InfoPedido },
  methods: {
    onShowModal() {
      this.$nextTick(async () => {
        if (this.$refs.infoPedido) {
          try {
            // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
            const isClienteFinal = isPerfilClienteAtivo();

            // Se for cliente final, carregar dados específicos
            if (isClienteFinal) {
              console.log('Modal aberto com perfil ClienteFinal, carregando dados específicos...');

              // Carregar dados salvos, se disponíveis
              const userId = this.getUserId();
              const savedFormData = localStorage.getItem(`infoPedidoForm_${userId}`);

              if (savedFormData) {
                try {
                  const parsedData = JSON.parse(savedFormData);
                  console.log('Dados salvos encontrados:', parsedData);

                  // Atualizar o formulário com os dados salvos
                  this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', parsedData);

                  // Garantir que os campos estejam desabilitados
                  this.$store.commit('InfoPedido/SET_CLIENTE_READONLY', true);
                  this.$store.commit('InfoPedido/SET_REPRESENTANTE_READONLY', true);

                  // Cache parcial de cliente não deve disparar consulta à carteira
                  // de representante. Reidrata pelo vínculo direto usuario-cliente.
                  if (!parsedData.cliente?.clienteid
                    || (this.$store.getters['preferencias/condicaoPagamentoAtiva'] && !parsedData.condicaoPagamento?.condicaopagamentoid)
                    || !parsedData.tabelaPreco?.tabelapreco_id) {
                    await InitialDataService.loadClienteFinalData();
                  }
                } catch (error) {
                  console.error('Erro ao processar dados salvos:', error);
                }
              } else {
                // Não há dados salvos, buscar novos dados
                console.log('Nenhum dado salvo encontrado, carregando dados novos...');
                const result = await InitialDataService.loadClienteFinalData();

                if (result && result.representante) {
                  console.log('Representante carregado com sucesso:', result.representante);
                }
              }
            }

            // Reset do estado de edição após todas as operações
            this.$refs.infoPedido.resetEditState();
          } catch (error) {
            console.error('Erro ao preparar modal:', error);
          }
        }
      });
    },
    limparDadosAnteriores() {
      const userId = this.getUserId();
      const savedFormData = localStorage.getItem(`infoPedidoForm_${userId}`);

      if (!savedFormData) {
        this.$store.dispatch('InfoPedido/resertInfoPedido');
      } else {
        try {
          const parsedData = JSON.parse(savedFormData);
          if (parsedData && Object.keys(parsedData).length > 0) {
            console.log('Carregando dados salvos do formulário:', parsedData);
            this.$store.commit('InfoPedido/SET_FORM_INFO_PEDIDO', parsedData);

            const isClienteFinal = isPerfilClienteAtivo();
            const isRepresentante = isPerfilRepresentanteAtivo();

            if (isClienteFinal) {
              this.$store.commit('InfoPedido/SET_CLIENTE_READONLY', true);
              this.$store.commit('InfoPedido/SET_REPRESENTANTE_READONLY', true);
            } else if (isRepresentante) {
              this.$store.commit('InfoPedido/SET_REPRESENTANTE_READONLY', true);
            }
          } else {
            this.$store.dispatch('InfoPedido/resertInfoPedido');
          }
        } catch (error) {
          console.error('Erro ao processar dados salvos:', error);
          this.$store.dispatch('InfoPedido/resertInfoPedido');
        }
      }
    },
    confirmarPedido() {
      this.$store.commit('InfoPedido/SET_IS_CONFIRMA_PEDIDO', true);

      const formData = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'];

      if (this.$store.getters['preferencias/condicaoPagamentoAtiva'] && !formData.condicaoPagamento?.condicaopagamentoid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validação',
            text: 'É necessário selecionar uma condição de pagamento válida',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      if (!formData.tabelaPreco || !formData.tabelaPreco.tabelapreco_id) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validação',
            text: 'É necessário selecionar uma tabela de preço válida',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      // Sem endereço cadastrado não é possível calcular o imposto — bloqueia a
      // conclusão do pedido. Considera "sem endereço" quando não há objeto de
      // endereço de entrega ou ele não tem logradouro nem UF.
      const enderecoEntrega = formData.enderecoEntrega;
      const temEndereco = !!enderecoEntrega
        && typeof enderecoEntrega === 'object'
        && (
          (!!enderecoEntrega.clienteendereco && String(enderecoEntrega.clienteendereco).trim() !== '')
          || (!!enderecoEntrega.ufid && String(enderecoEntrega.ufid).trim() !== '')
        );
      if (!temEndereco) {
        Swal.fire({
          title: 'Cliente sem endereço cadastrado',
          text: 'Não é possível concluir o pedido: este cliente não possui endereço cadastrado e, sem o endereço, não é possível calcular o imposto. Cadastre o endereço do cliente antes de prosseguir.',
          icon: 'error',
          confirmButtonText: 'Entendi',
        });
        return;
      }

      this.$store.commit('InfoPedido/SET_CLIENTE_DATA', {
        clienteData: formData,
        enderecoEntrega: formData.enderecoEntrega,
        dataCapaPedido: {
          estabelecimentoid: formData.estabelecimento.estabelecimentoid,
          clientenomeabreviado: formData.cliente.clientenomeabreviado,
          naturezaoperacaoid: formData.naturezaOperacao?.naturezaoperacaoid || '',
          canal_venda_id: formData.cliente.canal_venda_id,
          clienteisentoie: formData.cliente.clienteisentoie,
          tabelaprecoid: formData.tabelaPreco.tabelapreco_id,
          condicaopagamentoid: formData.condicaoPagamento.condicaopagamentoid,
        },
      });

      if (this.$store.getters['auth/IS_LOGGED_IN']) {
        const userId = this.getUserId();
        const sessionId = InitialDataService.getSessionId();
        const cacheData = {
          ...formData,
          sessionId,
          timestamp: Date.now(),
        };
        localStorage.setItem(`infoPedidoForm_${userId}`, JSON.stringify(cacheData));
      }

      this.$refs.refModalCenterInfoPedido.hide();

      this.$root.$emit('info-pedido:updated', formData);
    },
    onCloseModal() {
      const formData = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'];

      this.$store.commit('InfoPedido/SET_CLIENTE_DATA', {
        clienteData: formData,
        enderecoEntrega: formData.enderecoEntrega,
        dataCapaPedido: {
          estabelecimentoid: formData.estabelecimento.estabelecimentoid,
          clientenomeabreviado: formData.cliente.clientenomeabreviado,
          naturezaoperacaoid: formData.naturezaOperacao?.naturezaoperacaoid || '',
          canal_venda_id: formData.cliente.canal_venda_id,
          clienteisentoie: formData.cliente.clienteisentoie,
          tabelaprecoid: formData.tabelaPreco.tabelapreco_id,
          condicaopagamentoid: formData.condicaoPagamento.condicaopagamentoid,
        },
      });

      if (this.$store.getters['auth/IS_LOGGED_IN']) {
        const userId = this.getUserId();
        const sessionId = InitialDataService.getSessionId();
        const cacheData = {
          ...formData,
          sessionId,
          timestamp: Date.now(),
        };
        localStorage.setItem(`infoPedidoForm_${userId}`, JSON.stringify(cacheData));
      }

      // this.$store.dispatch('InfoPedido/resertInfoPedido')
    },
    getUserId() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        return userData?.id || userData?.userData?.id || 'default';
      } catch (error) {
        console.error('Erro ao obter ID do usuário:', error);
        return 'default';
      }
    },
  },
  computed: {
    carrinhoComItens() {
      return this.$store.getters['PedidosStore/getTotalItensPedido'] > 0;
    },
    itensCreatePedidoExistem() {
      const itens = this.$store.getters['intensPedido/getlistarTabelaItens'];
      return Array.isArray(itens) && itens.length > 0;
    },
    bloqueioGlobal() {
      return this.carrinhoComItens || this.itensCreatePedidoExistem;
    },
    isClienteFinal() {
      // O perfil de cliente final pode se chamar 'Cliente' OU 'ClienteFinal' (varia por tenant).
      return isPerfilClienteAtivo();
    },
  },
};
</script>

<template>
  <b-modal id="modal-center-info-pedido" ref="refModalCenterInfoPedido" size="xl" title="Info. Pedido"
    @close="onCloseModal" @cancel="onCloseModal" @show="onShowModal" footer-class="d-flex justify-content-between"
    no-close-on-backdrop>
    <info-pedido ref="infoPedido" :bloqueado="bloqueioGlobal" />

    <template #modal-footer>
      <div>
        <b-button variant="outline-secondary" @click="$refs.refModalCenterInfoPedido.hide()">
          Fechar
        </b-button>
        <b-button variant="primary" class="ml-2" @click="confirmarPedido"
          :disabled="!$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'] || bloqueioGlobal">
          Confirmar
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<style scoped lang="scss"></style>
